import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  keyframes
} from '@mui/material';
import { CheckCircle, Add, Remove, ShoppingCart } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';

function Pricing() {
  const pulseGlow = keyframes`
    0% {
      box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(79, 70, 229, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(79, 70, 229, 0);
    }
  `;

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const planCode = params.get("plan");
  const isOverage = params.get("type") === "overage";
  const { user, isAuthenticated, setUser } = useAuth();
  const [loading, setLoading] = useState({});
  const [fetchingPlans, setFetchingPlans] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [plans, setPlans] = useState([]);
  const [overageQty, setOverageQty] = useState(1);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [limitPopupOpen, setLimitPopupOpen] = useState(false);
  const [limitPopupMessage, setLimitPopupMessage] = useState('');
  const [overageUsageData, setOverageUsageData] = useState(null);
  const [overageUsageLoading, setOverageUsageLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starterConfirmOpen, setStarterConfirmOpen] = useState(false);

  const PRO_OVERAGE_MAX     = 100;
  const STARTER_OVERAGE_MAX = 20;

  const handleQuantityChange = (val) => {
    const newVal = Math.max(1, Math.min(100, val));
    setQuantity(newVal);
  };

  React.useEffect(() => {
    fetchPlans();
  }, []);

  React.useEffect(() => {
    if (isOverage && (planCode === 'professional' || planCode === 'starter') && isAuthenticated) {
      fetchOverageUsage();
    }
  }, [isOverage, planCode, isAuthenticated]);

  const fetchOverageUsage = async () => {
    try {
      setOverageUsageLoading(true);
      const res = await api.get('/applications/overage-usage');
      setOverageUsageData(res.data);
    } catch (err) {
      console.error('Failed to fetch overage usage:', err);
    } finally {
      setOverageUsageLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      setFetchingPlans(true);
      const paymentBasePath = (api.defaults.baseURL || '').endsWith('/api')
        ? '/payment'
        : '/api/payment';
      
      const response = await api.get(`${paymentBasePath}/plans`);
      const backendPlans = response.data.plans || [];
      
      // Filter for recruiter/admin plans only (exclude candidate plans for this page)
      const recruiterPlans = backendPlans.filter(p => !p.code.startsWith('CANDIDATE_'));

      // Transform backend plans to UI format
      const mappedPlans = recruiterPlans.map(p => {
        let period = '';
        if (p.code === 'MONTHLY') period = '/ Month';
        else if (p.code === 'ANNUAL') period = '/ Year';
        else if (p.code === 'PAY_AS_YOU_GO') period = 'per active job';

        return {
          title: p.name,
          price: p.amount === 0 ? '₹0' : `₹${(p.amount / 100).toLocaleString()}`,
          period: period,
          planCode: p.code.toLowerCase(),
          description: p.description,
          features: p.features || [],
          overage: p.overage || 'Not Applicable',
          buttonText: 'Get Started',
          variant: p.code === 'ANNUAL' ? 'contained' : 'outlined',
          popular: p.code === 'ANNUAL'
        };
      });

      setPlans(mappedPlans);
    } catch (err) {
      console.error('Failed to fetch pricing plans:', err);
      setError('Failed to load pricing plans. Please try again later.');
    } finally {
      setFetchingPlans(false);
    }
  };

  async function handleSubscribe(planCode, qty = 1){
    if (!isAuthenticated) {
      setError('Please login first to subscribe.');
      navigate('/login');
      return;
    }

    if (planCode === 'free') {
      if (user?.has_used_free_trial) {
        setError('You have already used your one-time free trial.');
        return;
      }
      navigate('/free-trial-confirm');
      return;
    }

    setLoading(prev => ({ ...prev, [planCode]: true }));
    setError('');

    try {
      // Support both local Express (baseURL ends with /api, routes under /payment)
      // and production API Gateway/Lambda (routes under /api/payment)
      const paymentBasePath = (api.defaults.baseURL || '').endsWith('/api')
        ? '/payment'
        : '/api/payment';

      const orderRes = await api.post(`${paymentBasePath}/create-order`, {
        planCode: planCode.toUpperCase(),
        quantity: qty
      });
      const { orderId, amount, currency, key } = orderRes.data;

      if (!orderId || !key) {
        throw new Error('Invalid response from payment server. Please try again later.');
      }

      const options = {
        key: key,
        amount: amount,
        currency: currency,
        name: 'ExiHire',
        description:
          planCode === 'pay_as_you_go'
            ? `${quantity} Job Credit(s) - Pay-As-You-Go`
            : planCode === 'OVERAGE_JOB'
            ? `${qty} Extra Job Credit(s)`
            : `${planCode.charAt(0).toUpperCase() + planCode.slice(1)} Subscription`,
        order_id: orderId,
        handler: async function (response) {
          try {
            const verifyRes = await api.post(`${paymentBasePath}/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planCode: planCode.toUpperCase(),
              quantity: qty,
              purchaseType:
                planCode === 'OVERAGE_JOB'
                  ? 'overage'
                  : 'subscription'
            });

            if (verifyRes.data.success) {
              try {
                const userRes = await api.get('/auth/user');
                setUser(userRes.data);
              } catch (e) {
                console.error("Failed to refresh user", e);
              }

              const fromAdminRequest = localStorage.getItem('fromAdminRequest');

              if (fromAdminRequest === 'true') {
                localStorage.removeItem('fromAdminRequest');

                setSuccessMessage(
                  'Form successfully submitted and payment received. Your request is pending super admin approval.'
                );

                setSuccessOpen(true);

                setTimeout(() => {
                  navigate('/candidate/dashboard');
                }, 2000);

              } else {

                setSuccessMessage('Subscription successful!');

                setSuccessOpen(true);

                setTimeout(() => {
                  navigate(
                    user?.role === 'admin' || user?.role === 'recruiter'
                      ? '/admin/dashboard'
                      : '/candidate/dashboard'
                  );
                }, 2000);
              }
            }
          } catch (err) {
            console.error('Payment verification failed:', err);
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: `${user?.firstName || ''} ${user?.lastName || ''}`,
          email: user?.email || '',
        },
        theme: {
          color: '#4F46E5',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Failed to initiate subscription:', err);

      // Better message for CORS / API Gateway mapping failures (no response)
      if (!err.response) {
        setError('Unable to connect to the payment service. Please try again in a moment.');
      } else {
        setError(
          err.response?.data?.message ||
            'Failed to initiate subscription. Please try again.'
        );
      }
    } finally {
      setLoading(prev => ({ ...prev, [planCode]: false }));
    }
    }
  // Professional overage computed values (derived from API data)
  const proPurchasedTotal = overageUsageData?.purchasedJobs || 0;
  const proRemainingAvailable = Math.max(PRO_OVERAGE_MAX - proPurchasedTotal, 0);
  const proExceedsLimit = overageQty > proRemainingAvailable;
  const proAtLimit = proRemainingAvailable === 0;

  const starterPurchasedTotal = overageUsageData?.purchasedJobs || 0;
  const starterRemainingAvailable = Math.max(STARTER_OVERAGE_MAX - starterPurchasedTotal, 0);
  const starterExceedsLimit = overageQty > starterRemainingAvailable;
  const starterAtLimit = starterRemainingAvailable === 0;



  const isProfessionalUpgrade = !isOverage && planCode === "professional";

  return (
    <Container sx={{ mt: 0, py: { xs: 4, md: 8 } }}>
      {isProfessionalUpgrade ? (
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
            borderRadius: '999px', bgcolor: '#f5f3ff', border: '1px solid #e9d5ff', mb: 2,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7c3aed' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Professional Plan
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
            Unlock Professional Features
          </Typography>
          <Typography sx={{ color: '#6b7280', maxWidth: 480, mx: 'auto', fontSize: '0.95rem', lineHeight: 1.7 }}>
            Get full access to Candidate Comparison, Advanced Analytics, Export Reports, and AI-powered hiring insights.
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontWeight: "bold",
            color: "#0A0F2C",
            mb: 4,
            fontSize: { xs: "2rem", md: "3rem" },
          }}
          gutterBottom
        >
          Subscription Plans
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
      {fetchingPlans ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid container spacing={4} alignItems="stretch" justifyContent="center">
          {plans
            .filter((plan) => {
              if (isOverage) {
                if (planCode === "starter") {
                  return plan.planCode === "monthly";
                }
                if (planCode === "professional") {
                  return plan.planCode === "annual";
                }
              }
              if (isProfessionalUpgrade) {
                return plan.planCode === "annual";
              }
              return true;
            })
            .map((plan, index) => (
            <Grid item key={index} xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: plan.popular ? '2px solid #4F46E5' : 'none',
                  boxShadow: plan.popular ? 6 : 1,
                  transform: { md: plan.popular ? 'scale(1.05)' : 'none', xs: 'none' },
                  mt: { xs: plan.popular ? 2 : 0, md: 0 },
                  zIndex: plan.popular ? 1 : 0,
                  width: '100%',
                  borderRadius: 2
                }}
              >
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 9,
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: '#4F46E5',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {plan.planCode === 'annual' ? 'RECOMMENDED' : 'MOST POPULAR'}
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography variant="h5" component="h2" align="center" gutterBottom fontWeight="bold">
                  {plan.title}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 2 }}>
                  <Typography variant="h3" component="span" fontWeight="bold">
                    {plan.planCode === 'pay_as_you_go' ? `₹${99 * quantity}` : plan.price}
                  </Typography>
                  <Typography variant="subtitle1" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {plan.planCode === 'pay_as_you_go' ? (quantity > 1 ? `for ${quantity} jobs` : plan.period) : plan.period}
                  </Typography>
                </Box>
                {plan.planCode === 'annual' && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      mt: -1,
                      py: '10px',
                      px: '20px',
                      borderRadius: '12px',
                      background: 'linear-gradient(90deg, #4F46E5, #60A5FA)',
                      color: 'white',
                      boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)',
                      width: 'fit-content',
                      maxWidth: '100%',
                      mx: 'auto',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        textAlign: 'center'
                      }}
                    >
                      Launch Offer
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        textAlign: 'center'
                      }}
                    >
                      Exclusive Pricing for First 100 Customers
                    </Typography>
                  </Box>
                )}
                {plan.planCode === 'pay_as_you_go' && (
                  <Box 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'rgba(0, 139, 139, 0.05)',
                      border: '1px solid rgba(0, 139, 139, 0.1)',
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center' 
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mb: 1.5, 
                        fontWeight: 'bold', 
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        color: 'text.secondary'
                      }}
                    >
                      Number of Jobs
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' }}>
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => handleQuantityChange(quantity - 1)}
                        sx={{ 
                          minWidth: 32, 
                          width: 32, 
                          height: 32, 
                          p: 0, 
                          borderRadius: '50%',
                          bgcolor: '#4F46E5',
                          '&:hover': { bgcolor: '#4338CA' }
                        }}
                      >
                        <Remove fontSize="small" />
                      </Button>
                      <TextField
                        value={quantity}
                        onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                        size="small"
                        variant="standard"
                        InputProps={{ 
                          disableUnderline: true,
                        }}
                        sx={{ 
                          width: 60, 
                          '& .MuiInputBase-input': { 
                            textAlign: 'center', 
                            fontSize: '1.25rem',
                            fontWeight: 'bold',
                            color: '#4F46E5',
                            py: 0
                          } 
                        }}
                        inputProps={{ min: 1, max: 100 }}
                      />
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => handleQuantityChange(quantity + 1)}
                        sx={{ 
                          minWidth: 32, 
                          width: 32, 
                          height: 32, 
                          p: 0, 
                          borderRadius: '50%',
                          bgcolor: '#4F46E5',
                          '&:hover': { bgcolor: '#4338CA' }
                        }}
                      >
                        <Add fontSize="small" />
                      </Button>
                    </Box>
                  </Box>
                )}
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
                  {plan.description}
                </Typography>
                <List sx={{ mb: 4 }}>
                  {plan.features.map((feature, idx) => (
                    <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle sx={{ color: '#4F46E5', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {plan.example && (
                  <Typography variant="caption" display="block" align="center" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                    {plan.example}
                  </Typography>
                )}
                {plan.overage && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Overage:
                    </Typography>
                    {plan.overage.split(' | ').map((line, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        {line}
                      </Typography>
                    ))}
                  </Box>
                )}
                {/* ── PROFESSIONAL OVERAGE UI ── */}
                {isOverage && plan.planCode === "annual" && (
                  <Box sx={{ mt: 2, p: 2.5, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Buy Extra Jobs</Typography>

                    {/* Max allowed info */}
                    <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                        Maximum Add-ons Allowed: {PRO_OVERAGE_MAX} Jobs
                      </Typography>
                    </Box>

                    {/* Usage status */}
                    {overageUsageLoading ? (
                      <CircularProgress size={18} sx={{ my: 1 }} />
                    ) : (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Used Add-ons: <strong>{proPurchasedTotal} / {PRO_OVERAGE_MAX}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Remaining Available: <strong style={{ color: proAtLimit ? '#d32f2f' : '#2e7d32' }}>{proRemainingAvailable}</strong>
                        </Typography>
                      </Box>
                    )}

                    {/* Quantity selector */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={overageQty <= 1}
                        onClick={() => setOverageQty(Math.max(1, overageQty - 1))}
                        sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '50%' }}
                      >
                        <Remove fontSize="small" />
                      </Button>
                      <TextField
                        value={overageQty}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(proRemainingAvailable || 1, parseInt(e.target.value) || 1));
                          setOverageQty(v);
                        }}
                        disabled={proAtLimit}
                        size="small"
                        variant="outlined"
                        inputProps={{ min: 1, max: proRemainingAvailable, style: { textAlign: 'center', width: 48, fontWeight: 'bold' } }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={proAtLimit || overageQty >= proRemainingAvailable}
                        onClick={() => setOverageQty(Math.min(proRemainingAvailable, overageQty + 1))}
                        sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '50%' }}
                      >
                        <Add fontSize="small" />
                      </Button>
                    </Box>

                    {/* Validation message */}
                    {proAtLimit && (
                      <Typography variant="body2" sx={{ color: '#d32f2f', mt: 1, fontWeight: 600 }}>
                        Add-on limit reached ({PRO_OVERAGE_MAX}/{PRO_OVERAGE_MAX}).
                      </Typography>
                    )}
                    {!proAtLimit && proExceedsLimit && (
                      <Typography variant="body2" sx={{ color: '#d32f2f', mt: 1, fontWeight: 600 }}>
                        Only {proRemainingAvailable} add-on{proRemainingAvailable !== 1 ? 's' : ''} remaining for your account.
                      </Typography>
                    )}

                    {/* Total */}
                    <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 600 }}>
                      Total: ₹{overageQty * 29}
                    </Typography>

                    {/* Pay button */}
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={proAtLimit || proExceedsLimit || overageUsageLoading}
                      onClick={() => setConfirmOpen(true)}
                      sx={{ mt: 2, bgcolor: '#4F46E5', fontWeight: 'bold', '&:hover': { bgcolor: '#4338CA' } }}
                    >
                      Pay ₹{overageQty * 29}
                    </Button>
                  </Box>
                )}

                {/* ── STARTER OVERAGE UI ── */}
                {isOverage && plan.planCode === "monthly" && (
                  <Box sx={{ mt: 2, p: 2.5, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Buy Extra Jobs</Typography>

                    <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                        Maximum Add-ons Allowed: {STARTER_OVERAGE_MAX} Jobs
                      </Typography>
                    </Box>

                    {overageUsageLoading ? (
                      <CircularProgress size={18} sx={{ my: 1 }} />
                    ) : (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Used Add-ons: <strong>{starterPurchasedTotal} / {STARTER_OVERAGE_MAX}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Remaining Available: <strong style={{ color: starterAtLimit ? '#d32f2f' : '#2e7d32' }}>{starterRemainingAvailable}</strong>
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={overageQty <= 1}
                        onClick={() => setOverageQty(Math.max(1, overageQty - 1))}
                        sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '50%' }}
                      >
                        <Remove fontSize="small" />
                      </Button>
                      <TextField
                        value={overageQty}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(starterRemainingAvailable || 1, parseInt(e.target.value) || 1));
                          setOverageQty(v);
                        }}
                        disabled={starterAtLimit}
                        size="small"
                        variant="outlined"
                        inputProps={{ min: 1, max: starterRemainingAvailable, style: { textAlign: 'center', width: 48, fontWeight: 'bold' } }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={starterAtLimit || overageQty >= starterRemainingAvailable}
                        onClick={() => setOverageQty(Math.min(starterRemainingAvailable, overageQty + 1))}
                        sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '50%' }}
                      >
                        <Add fontSize="small" />
                      </Button>
                    </Box>

                    {starterAtLimit && (
                      <Typography variant="body2" sx={{ color: '#d32f2f', mt: 1, fontWeight: 600 }}>
                        Add-on limit reached ({STARTER_OVERAGE_MAX}/{STARTER_OVERAGE_MAX}).
                      </Typography>
                    )}
                    {!starterAtLimit && starterExceedsLimit && (
                      <Typography variant="body2" sx={{ color: '#d32f2f', mt: 1, fontWeight: 600 }}>
                        Only {starterRemainingAvailable} add-on{starterRemainingAvailable !== 1 ? 's' : ''} remaining for your account.
                      </Typography>
                    )}

                    <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 600 }}>
                      Total: ₹{overageQty * 39}
                    </Typography>

                    <Button
                      variant="contained"
                      fullWidth
                      disabled={starterAtLimit || starterExceedsLimit || overageUsageLoading}
                      onClick={() => setStarterConfirmOpen(true)}
                      sx={{ mt: 2, bgcolor: '#4F46E5', fontWeight: 'bold', '&:hover': { bgcolor: '#4338CA' } }}
                    >
                      Pay ₹{overageQty * 39}
                    </Button>
                  </Box>
                )}
              </CardContent>
              <Box sx={{ p: 3, mt: 'auto' }}>
                <Button
                  fullWidth
                  variant={plan.variant}
                  size="large"
                  color="primary"
                  disabled={loading[plan.planCode]}
                  onClick={() => {
                    if (plan.planCode === 'free') {
                      setError('');

                      if (user?.hasUsedFreeTrial) {
                        setError("You have already used your free trial. Upgrade your plan.");
                        return;
                      }

                      navigate('/free-trial-confirm');
                    } else {
                            // FIX: Pass the 'quantity' state variable for Pay-As-You-Go
                            const selectedQty = plan.planCode === 'pay_as_you_go' ? quantity : 1;
                            handleSubscribe(plan.planCode, selectedQty);
                          }
                  }}
                  startIcon={plan.planCode !== 'free' ? <ShoppingCart /> : null}
                  sx={{
                    py: 1.5,
                    fontWeight: 'bold',
                    borderRadius: 2,
                    boxShadow: plan.variant === 'contained' ? '0 4px 14px 0 rgba(79,70,229,0.39)' : 'none',
                    '&:hover': {
                      boxShadow: plan.variant === 'contained' ? '0 6px 20px rgba(79,70,229,0.23)' : 'none',
                    }
                  }}
                >
                  {loading[plan.planCode] ? (
                    <CircularProgress size={24} />
                  ) : (
                    plan.buttonText
                  )}
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
        </Grid>
      )}

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#4F46E5',
            pt: 4
          }}
        >
          Payment Successful
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              textAlign: 'center',
              py: 2,
              fontSize: '16px'
            }}
          >
            {successMessage}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 4
          }}
        >
          <Button
            variant="contained"
            onClick={() => setSuccessOpen(false)}
            sx={{
              backgroundColor: '#4F46E5',
              px: 5,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#006d6d'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Professional Overage Confirmation Modal ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#4F46E5', pt: 3, pb: 1, textAlign: 'center' }}>
          Purchase Extra Jobs
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography color="text.secondary">Extra Jobs</Typography>
              <Typography fontWeight="bold">{overageQty}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography color="text.secondary">Price Per Job</Typography>
              <Typography fontWeight="bold">₹29</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography color="text.secondary">Total Amount</Typography>
              <Typography fontWeight="bold" color="#4F46E5">₹{overageQty * 29}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography color="text.secondary">Remaining After Purchase</Typography>
              <Typography fontWeight="bold">{Math.max(proRemainingAvailable - overageQty, 0)}</Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Each extra job includes 5 applications.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Add-ons expire in 6 months.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmOpen(false)}
            sx={{ flex: 1, borderColor: '#ccc', color: '#555', '&:hover': { borderColor: '#999' } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { setConfirmOpen(false); handleSubscribe("OVERAGE_JOB", overageQty); }}
            sx={{ flex: 1, bgcolor: '#4F46E5', fontWeight: 'bold', '&:hover': { bgcolor: '#4338CA' } }}
          >
            Proceed Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Starter Overage Confirmation Modal ── */}
      <Dialog open={starterConfirmOpen} onClose={() => setStarterConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#4F46E5', pt: 3, pb: 1, textAlign: 'center' }}>
          Purchase Extra Jobs
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography color="text.secondary">Extra Jobs</Typography>
              <Typography fontWeight="bold">{overageQty}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography color="text.secondary">Price Per Job</Typography>
              <Typography fontWeight="bold">₹39</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography color="text.secondary">Total Amount</Typography>
              <Typography fontWeight="bold" color="#4F46E5">₹{overageQty * 39}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography color="text.secondary">Remaining After Purchase</Typography>
              <Typography fontWeight="bold">{Math.max(starterRemainingAvailable - overageQty, 0)}</Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Each extra job includes 5 applications.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Add-ons expire in 30 days.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setStarterConfirmOpen(false)}
            sx={{ flex: 1, borderColor: '#ccc', color: '#555', '&:hover': { borderColor: '#999' } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { setStarterConfirmOpen(false); handleSubscribe("OVERAGE_JOB", overageQty); }}
            sx={{ flex: 1, bgcolor: '#4F46E5', fontWeight: 'bold', '&:hover': { bgcolor: '#4338CA' } }}
          >
            Proceed Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={limitPopupOpen}
        onClose={() => setLimitPopupOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#d32f2f',
            pt: 4
          }}
        >
          Purchase Limit
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              textAlign: 'center',
              py: 2,
              fontSize: '16px'
            }}
          >
            {limitPopupMessage}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 4
          }}
        >
          <Button
            variant="contained"
            onClick={() => setLimitPopupOpen(false)}
            sx={{
              backgroundColor: '#4F46E5',
              px: 5,
              py: 1,
              borderRadius: 2,
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#006d6d'
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Pricing;
